export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

export interface LogConfig {
  level: LogLevel
  enableConsole: boolean
  enableRemote: boolean
  remoteEndpoint?: string
  modules?: {
    [moduleName: string]: LogLevel
  }
}

export interface LogEntry {
  timestamp: string
  level: string
  module: string
  message: string
  data?: any
  error?: Error
}

class Logger {
  private config: LogConfig = {
    level: LogLevel.INFO,
    enableConsole: true,
    enableRemote: false,
    modules: {}
  }

  private logBuffer: LogEntry[] = []
  private maxBufferSize = 100

  configure(config: Partial<LogConfig>) {
    this.config = { ...this.config, ...config }
  }

  private shouldLog(level: LogLevel, module: string): boolean {
    // Check module-specific log level first
    const moduleLevel = this.config.modules?.[module]
    const threshold = moduleLevel !== undefined ? moduleLevel : this.config.level
    
    return level >= threshold && this.config.level !== LogLevel.NONE
  }

  private createLogEntry(level: LogLevel, module: string, message: string, data?: any, error?: Error): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level: LogLevel[level],
      module,
      message,
      data,
      error
    }
  }

  private outputToConsole(entry: LogEntry) {
    if (!this.config.enableConsole) return

    const timestamp = new Date(entry.timestamp).toLocaleTimeString()
    const prefix = `[${timestamp}] [${entry.level}] [${entry.module}]`
    
    switch (entry.level) {
      case 'DEBUG':
        console.debug(`%c${prefix}`, 'color: #888', entry.message, entry.data || '')
        break
      case 'INFO':
        console.log(`%c${prefix}`, 'color: #0066cc', entry.message, entry.data || '')
        break
      case 'WARN':
        console.warn(`%c${prefix}`, 'color: #ff8800', entry.message, entry.data || '')
        break
      case 'ERROR':
        console.error(`%c${prefix}`, 'color: #cc0000', entry.message, entry.data || '', entry.error || '')
        break
    }
  }

  private addToBuffer(entry: LogEntry) {
    this.logBuffer.push(entry)
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift()
    }
  }

  private async sendToRemote(entry: LogEntry) {
    if (!this.config.enableRemote || !this.config.remoteEndpoint) return

    try {
      await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(entry)
      })
    } catch (error) {
      // Fallback to console if remote logging fails
      console.error('Failed to send log to remote endpoint:', error)
    }
  }

  private log(level: LogLevel, module: string, message: string, data?: any, error?: Error) {
    if (!this.shouldLog(level, module)) return

    const entry = this.createLogEntry(level, module, message, data, error)
    
    this.addToBuffer(entry)
    this.outputToConsole(entry)
    
    if (this.config.enableRemote) {
      this.sendToRemote(entry)
    }
  }

  debug(module: string, message: string, data?: any) {
    this.log(LogLevel.DEBUG, module, message, data)
  }

  info(module: string, message: string, data?: any) {
    this.log(LogLevel.INFO, module, message, data)
  }

  warn(module: string, message: string, data?: any) {
    this.log(LogLevel.WARN, module, message, data)
  }

  error(module: string, message: string, error?: Error, data?: any) {
    this.log(LogLevel.ERROR, module, message, data, error)
  }

  // Utility methods
  getLogBuffer(): LogEntry[] {
    return [...this.logBuffer]
  }

  clearBuffer() {
    this.logBuffer = []
  }

  setLogLevel(level: LogLevel) {
    this.config.level = level
  }

  setModuleLogLevel(module: string, level: LogLevel) {
    if (!this.config.modules) {
      this.config.modules = {}
    }
    this.config.modules[module] = level
  }

  enableModule(module: string) {
    this.setModuleLogLevel(module, LogLevel.DEBUG)
  }

  disableModule(module: string) {
    this.setModuleLogLevel(module, LogLevel.NONE)
  }
}

// Create singleton instance
export const logger = new Logger()

// Module-specific loggers for convenience
export const createModuleLogger = (moduleName: string) => ({
  debug: (message: string, data?: any) => logger.debug(moduleName, message, data),
  info: (message: string, data?: any) => logger.info(moduleName, message, data),
  warn: (message: string, data?: any) => logger.warn(moduleName, message, data),
  error: (message: string, error?: Error, data?: any) => logger.error(moduleName, message, error, data),
})

// Common module loggers
export const authLogger = createModuleLogger('AUTH')
export const apiLogger = createModuleLogger('API')
export const uiLogger = createModuleLogger('UI')
export const offlineLogger = createModuleLogger('OFFLINE')
